import {
  ForecastItem,
  ForecastUpdateDto,
  RealItem,
  RealUpdateDto,
  GroupDailyStatsDTO,
  GroupDailyStatsUpsertByDateDTO,
  CategoryDailyStatsDTO,
  CategoryDailyStatsUpsertByDateDTO,
} from "../types/stat.d";
import axios from "../utils/axiosInstance";

export const StatService = {
  async getForecast(
    hotelId: number | null,
    startDate: Date,
    endDate: Date
  ): Promise<ForecastItem[]> {
    const formattedStartDate = startDate.toISOString().split("T")[0];
    const formattedEndDate = endDate.toISOString().split("T")[0];
    const response = await axios.get("/ForecastResult/by-date", {
      params: {
        ...(hotelId != null && { hotelId }),
        startDate: formattedStartDate,
        endDate: formattedEndDate,
      },
    });
    return response.data;
  },

  async sendForecast(forecasts: ForecastUpdateDto[]): Promise<void> {
    try {
      await axios.post("/ForecastResult", forecasts, {
        headers: { "Content-Type": "application/json" },
      });
    } catch (error) {
      throw error;
    }
  },

  async getOccupation(
    hotelId: number | null,
    startDate: Date,
    endDate: Date
  ): Promise<RealItem[]> {
    const formattedStartDate = startDate.toISOString().split("T")[0];
    const formattedEndDate = endDate.toISOString().split("T")[0];
    const response = await axios.get("/RealResult/by-date", {
      params: {
        ...(hotelId != null && { hotelId }),
        startDate: formattedStartDate,
        endDate: formattedEndDate,
      },
    });
    return response.data;
  },

  async sendOccupation(reals: RealUpdateDto[]): Promise<void> {
    try {
      await axios.post("/RealResult", reals, {
        headers: { "Content-Type": "application/json" },
      });
    } catch (error) {
      throw error;
    }
  },

  async calculateForecast(params: {
    dates: string[];
    hotel_id?: number | null;
    period: number;
  }): Promise<any> {
    try {
      const response = await axios.post("/ai/forecast", params);
      // Backend proxy returns Python's payload. Keep compatibility if it wraps result under `forecast`.
      return (response.data && (response.data as any).forecast) ?? response.data;
    } catch (error) {
      console.error("Erreur lors du calcul des prévisions :", error);
      throw error;
    }
  },

  async generateTotalForecast(params: {
    dates: string[];
    hotel_id?: number;
    period: number;
  }): Promise<any> {
    try {
      const response = await axios.post("/ai/forecast/total", params);
      return response.data;
    } catch (error) {
      console.error(
        "Erreur lors de la génération des prévisions totales :",
        error
      );
      throw error;
    }
  },

  async generateRealOccupation(params: {
    dates: string[];
    hotel_id?: number;
  }): Promise<any> {
    try {
      const response = await axios.post("/ai/real", params);
      return response.data;
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des données réelles :",
        error
      );
      throw error;
    }
  },

  async generateVisitorOccupation(params: { dates: string[] }): Promise<any> {
    try {
      const response = await axios.post("/ai/real/visitor", params);
      return response.data;
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des statistiques des visiteurs :",
        error
      );
      throw error;
    }
  },

  async getVisitors(
    hotelId: number | null,
    startDate: Date,
    endDate: Date
  ) {
    const formattedStartDate = startDate.toISOString().split("T")[0];
    const formattedEndDate = endDate.toISOString().split("T")[0];
    const response = await axios.get("/VisitorCounter/by-date", {
      params: {
        ...(hotelId != null && { hotelId }),
        startDate: formattedStartDate,
        endDate: formattedEndDate,
      },
    });
    return response.data;
  },

  async upsertVisitorCountersByDate(items: Array<{
    hotelId: number;
    date: string; // yyyy-MM-dd
    visitorTypeId: number;
    visitorSubTypeId?: number | null;
    visitorGroupTypeId?: number | null;
    total: number;
  }>): Promise<void> {
    try {
      await axios.post("/VisitorCounter/by-date/upsert", items, {
        headers: { "Content-Type": "application/json" },
      });
    } catch (error) {
      console.error("Erreur lors de l'upsert des compteurs visiteurs:", error);
      throw error;
    }
  },
  async getGroupDailyStats(
    hotelId: number | null,
    startDate: Date,
    endDate: Date
  ): Promise<GroupDailyStatsDTO[]> {
    const formattedStartDate = startDate.toISOString().split("T")[0];
    const formattedEndDate = endDate.toISOString().split("T")[0];
    const response = await axios.get("/GroupDailyStats/by-date", {
      params: {
        ...(hotelId != null && { hotelId }),
        startDate: formattedStartDate,
        endDate: formattedEndDate,
      },
    });
    return response.data;
  },

  async upsertGroupDailyStatsByDate(items: GroupDailyStatsUpsertByDateDTO[]): Promise<void> {
    try {
      await axios.post("/GroupDailyStats/by-date/upsert", items, {
        headers: { "Content-Type": "application/json" },
      });
    } catch (error) {
      console.error("Erreur lors de l'upsert des GroupDailyStats:", error);
      throw error;
    }
  },

  async getCategoryDailyStats(
    hotelId: number | null,
    startDate: Date,
    endDate: Date
  ): Promise<CategoryDailyStatsDTO[]> {
    const formattedStartDate = startDate.toISOString().split("T")[0];
    const formattedEndDate = endDate.toISOString().split("T")[0];
    const response = await axios.get("/CategoryDailyStats/by-date", {
      params: {
        ...(hotelId != null && { hotelId }),
        startDate: formattedStartDate,
        endDate: formattedEndDate,
      },
    });
    return response.data;
  },

  async upsertCategoryDailyStatsByDate(items: CategoryDailyStatsUpsertByDateDTO[]): Promise<void> {
    try {
      await axios.post("/CategoryDailyStats/by-date/upsert", items, {
        headers: { "Content-Type": "application/json" },
      });
    } catch (error) {
      console.error("Erreur lors de l'upsert des CategoryDailyStats:", error);
      throw error;
    }
  },
};

export default StatService;
